import { OffOrgNavigation } from "@/components/OffOrgNavigation";
import { ACCOUNT_NAV_SECTIONS } from "@/config/accountNavigation";
import { SYSTEM_NAV_SECTIONS } from "@/config/systemNavigation";
import { MainNavigation } from "@/components/MainNavigation";
import { UnifiedHeader } from "@/components/UnifiedHeader";
import { Footer } from "@/components/Footer";
import { useRenderDebug } from "@/hooks/useRenderDebug";
import { PageHelpProvider } from "@/components/ui/page-help";

interface AppLayoutProps {
  children: React.ReactNode;
  /**
   * Opt-in for pages that own their own scrolling (the project event planner
   * timeline): the shared scroll container stops scrolling, the page gets the
   * full remaining height as a bounded flex column, and the Footer is omitted.
   * Rides the existing `html, body, #root { h-full }` chain — no viewport
   * heights (docs/LAYOUT.md "AppLayout fullHeight"). Default off: every other
   * page keeps the single scroll container + footer.
   */
  fullHeight?: boolean;
  /**
   * Which sidebar: the org sidebar (default), or the off-org sidebar for pages
   * outside `/org/:orgId` whose org links would have no organization to point
   * at — `account` for the per-user pages (`/profile`), `system` for System
   * Settings (docs/NAVIGATION.md "Off-org navigation").
   */
  navigation?: "org" | "account" | "system";
}

export function AppLayout({ children, fullHeight = false, navigation = "org" }: AppLayoutProps) {
  // DEBUG: Track what's causing re-renders
  useRenderDebug('AppLayout', { children });

  return (
    <div className="h-full flex flex-col w-full overflow-hidden">
      <UnifiedHeader />
      <div className={fullHeight ? "flex-1 min-h-0 overflow-hidden" : "flex-1 overflow-y-auto overflow-x-hidden"}>
        <div className={fullHeight ? "h-full flex flex-col" : "min-h-full flex flex-col"}>
          <div className={fullHeight ? "flex flex-1 min-h-0" : "flex flex-1"}>
            {navigation === "org" ? (
              <MainNavigation />
            ) : (
              <OffOrgNavigation sections={navigation === "system" ? SYSTEM_NAV_SECTIONS : ACCOUNT_NAV_SECTIONS} />
            )}
            <div className={fullHeight ? "flex-1 min-w-0 min-h-0 flex flex-col" : "flex-1 min-w-0"}>
              {/* A page's help reaches its PageHeader's "How it works" link (page-help.tsx). */}
              <PageHelpProvider>{children}</PageHelpProvider>
            </div>
          </div>
          {!fullHeight && <Footer />}
        </div>
      </div>
    </div>
  );
}
